<script setup lang="ts">
import { useI18n } from 'vue-i18n'
import { computed, ref } from 'vue'
import ReleaseConfirmationDialog from '@/components/dialogs/ReleaseConfirmationDialog.vue'
import { useMutation, useQuery } from '@vue/apollo-composable'
import gql from 'graphql-tag'
import {
  LibraryStepReleaseConfirmationDataQuery,
  LibraryStepReleaseConfirmationDataQueryVariables,
  LibraryStepReleaseConfirmationReleaseMutation,
  LibraryStepReleaseConfirmationReleaseMutationVariables,
  VersionStatus,
} from '@/generated/graphql'
import DataTable from '@/components/dataTable/DataTable.vue'
import DataTableSearch from '@/components/dataTable/DataTableSearch.vue'
import NotificationSaved from '@/components/notifications/NotificationSaved.vue'
import LibraryStepVersionChange from '@/components/step/LibraryStepVersionChange.vue'

const props = defineProps<{
  stepId: string
  modelValue: boolean
}>()
const emit = defineEmits<{
  'update:modelValue': [value: boolean]
  release: []
}>()

const { t } = useI18n()

const fetchQuery = useQuery<
  LibraryStepReleaseConfirmationDataQuery,
  LibraryStepReleaseConfirmationDataQueryVariables
>(
  gql`
    query LibraryStepReleaseConfirmationData($stepId: ID!) {
      product {
        step(id: $stepId) {
          id
          name
          libraryItemVersion {
            version
          }
        }
        libraryStepVersions(stepId: $stepId) {
          id
          libraryItemVersion {
            version
            versionStatus
          }
          usages {
            bop {
              id
              version
              versionStatus
              productConfiguration {
                id
                name
                archived
                product {
                  id
                  name
                }
                bops {
                  id
                  version
                  site {
                    id
                  }
                }
              }
              site {
                id
                name
              }
            }
          }
        }
      }
    }
  `,
  () => ({ stepId: props.stepId }),
)
const data = computed(() => fetchQuery.result.value?.product)
type UsedBop =
  LibraryStepReleaseConfirmationDataQuery['product']['libraryStepVersions'][number]['usages'][number]['bop']

// Versions come newest first.
const currentVersion = computed(
  () =>
    data.value?.libraryStepVersions.find(
      (version) => version.libraryItemVersion?.versionStatus == VersionStatus.Released,
    )?.libraryItemVersion?.version,
)
const replacesReleasedVersion = computed(() => currentVersion.value !== undefined)
// Like the Update button: only the newest version of a BOP that is not archived takes the update.
const canUpdate = (bop: UsedBop) =>
  !bop.productConfiguration.archived &&
  [...bop.productConfiguration.bops]
    .filter((b) => b.site.id == bop.site.id)
    .sort((a, b) => b.version - a.version)[0]?.id == bop.id
// One row per BOP; one that uses two versions keeps the oldest, as versions come newest first.
const affectedBops = computed(() => [
  ...new Map(
    (data.value?.libraryStepVersions ?? [])
      .flatMap((version) =>
        version.usages.map((usage) => ({
          bop: usage.bop,
          usedVersion: version.libraryItemVersion?.version,
        })),
      )
      .filter((row) => canUpdate(row.bop))
      .map((row) => [row.bop.id, row]),
  ).values(),
])
type AffectedBop = (typeof affectedBops.value)[number]

const notifySaved = ref(false)
const notifyReleased = ref(false)
const releasedBopCount = ref(0)
const mutation = useMutation<
  LibraryStepReleaseConfirmationReleaseMutation,
  LibraryStepReleaseConfirmationReleaseMutationVariables
>(gql`
  mutation LibraryStepReleaseConfirmationRelease($stepId: ID!) {
    product {
      releaseLatestLibraryStepVersion(stepId: $stepId) {
        id
      }
    }
  }
`)
function release() {
  releasedBopCount.value = affectedBops.value.length
  mutation.mutate({ stepId: props.stepId }).then(() => {
    if (replacesReleasedVersion.value) notifyReleased.value = true
    else notifySaved.value = true
    emit('update:modelValue', false)
    emit('release')
  })
}

const search = ref('')
// The search looks only at these columns, so the placeholder can name them.
const searchedHeaders = [
  { key: 'bop.productConfiguration.product.name', title: t('entity.product.singular') },
  { key: 'bop.productConfiguration.name', title: t('entity.productConfiguration.singular') },
  { key: 'bop.site.name', title: t('entity.site.singular') },
]
const searchKeys = searchedHeaders.map((header) => header.key)
const headers = [
  ...searchedHeaders,
  {
    key: 'bop.versionStatus',
    title: t('component.libraryStepReleaseConfirmation.bopStatus'),
    nowrap: true,
  },
  {
    key: 'usedVersion',
    title: t('component.libraryStepReleaseConfirmation.usedVersion'),
    nowrap: true,
  },
]
// Five rows per page keep the warning and the confirmation below the table in view.
const itemsPerPageOptions = [
  { value: 5, title: '5' },
  { value: 10, title: '10' },
  { value: 25, title: '25' },
  { value: 50, title: '50' },
]
const sort = [{ key: 'bop.productConfiguration.name', order: 'asc' as const }]
</script>

<template>
  <release-confirmation-dialog
    v-if="!!data"
    :model-value="props.modelValue"
    v-bind="$attrs"
    :max-width="replacesReleasedVersion ? 900 : 500"
    :title="
      replacesReleasedVersion
        ? t('component.libraryStepReleaseConfirmation.title')
        : t('view.organization.library.releaseTitle')
    "
    :message="
      replacesReleasedVersion
        ? t('component.libraryStepReleaseConfirmation.message')
        : t('view.organization.library.releaseMessage', {
            stepName: data.step.name,
            version: data.step.libraryItemVersion?.version,
          })
    "
    :loading="mutation.loading.value"
    @update:model-value="(v) => $emit('update:modelValue', v)"
    @release="release"
  >
    <template v-if="replacesReleasedVersion">
      <div class="text-subtitle-1 font-weight-medium mb-3">{{ data.step.name }}</div>
      <library-step-version-change
        :current-version="currentVersion"
        :new-version="data.step.libraryItemVersion?.version"
        class="mb-4"
      />
      <div class="d-flex align-center ga-3 mb-2">
        <i18n-t
          v-if="affectedBops.length"
          keypath="component.libraryStepReleaseConfirmation.affectedBops"
          :plural="affectedBops.length"
          tag="span"
        >
          <template #count>
            <strong>{{ affectedBops.length }}</strong>
          </template>
        </i18n-t>
        <span v-else>{{ t('view.organization.library.notInUse') }}</span>
        <v-spacer />
        <data-table-search
          v-if="affectedBops.length"
          v-model="search"
          :placeholder="t('component.libraryStepReleaseConfirmation.searchPlaceholder')"
        />
      </div>
      <data-table
        v-if="affectedBops.length"
        :items="affectedBops"
        :headers="headers"
        :sort-by="sort"
        :search="search"
        :filter-keys="searchKeys"
        item-value="bop.id"
        :items-per-page="5"
        :items-per-page-options="itemsPerPageOptions"
        density="comfortable"
        class="mb-4"
      >
        <template #item.bop.productConfiguration.name="{ item }: { item: AffectedBop }">
          <router-link
            :to="{
              name: 'bopProcess',
              params: {
                productId: item.bop.productConfiguration.product.id,
                productConfigId: item.bop.productConfiguration.id,
                siteId: item.bop.site.id,
                bopId: item.bop.id,
              },
            }"
            target="_blank"
          >
            {{ item.bop.productConfiguration.name }}
          </router-link>
        </template>
        <template #item.bop.versionStatus="{ item }: { item: AffectedBop }">
          <v-chip size="small" variant="tonal">
            {{ t(`component.versionStatus.status.${item.bop.versionStatus}`) }}
          </v-chip>
        </template>
        <template #item.usedVersion="{ item }: { item: AffectedBop }">
          v{{ item.usedVersion }}
        </template>
      </data-table>
    </template>
  </release-confirmation-dialog>

  <notification-saved v-model="notifySaved" />
  <v-snackbar v-model="notifyReleased" :timeout="5000">
    {{ t('component.libraryStepReleaseConfirmation.released', releasedBopCount) }}
  </v-snackbar>
</template>
